import execute from '../hooks/execute';
import { msalInstance } from '../index';
import { protectedResources } from '../config/msalConfig';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

/**
 * Template Service - API client for template management
 * Provides methods for CRUD operations, analytics, and template management
 */
const templateService = {
  /**
   * Get all templates with optional filtering
   * @param {Object} filters - Filter parameters (analysisLevel, scope, category, isActive, page, pageSize)
   * @returns {Promise<Object>} Response with templates array and pagination info
   */
  async getTemplates(filters = {}) {
    const query = new URLSearchParams(
      Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {})
    ).toString();

    const endpoint = `${BASE_URL}/v1/templates${query ? `?${query}` : ''}`;
    return await execute('GET', endpoint);
  },

  /**
   * Get single template by ID
   * @param {string} id - Template ID
   * @returns {Promise<Object>} Template object
   */
  async getTemplateById(id) {
    const endpoint = `${BASE_URL}/v1/templates/${id}`;
    return await execute('GET', endpoint);
  },

  /**
   * Create new template
   * @param {Object} data - Template data
   * @returns {Promise<Object>} Created template
   */
  async createTemplate(data) {
    const endpoint = `${BASE_URL}/v1/templates`;
    return await execute('POST', endpoint, data);
  },

  /**
   * Update existing template
   * @param {string} id - Template ID
   * @param {Object} data - Updated template data
   * @returns {Promise<Object>} Updated template
   */
  async updateTemplate(id, data) {
    const endpoint = `${BASE_URL}/v1/templates/${id}`;
    return await execute('PUT', endpoint, data);
  },

  /**
   * Delete template
   * @param {string} id - Template ID
   * @returns {Promise<Object>} Success response
   */
  async deleteTemplate(id) {
    const endpoint = `${BASE_URL}/v1/templates/${id}`;
    return await execute('DELETE', endpoint);
  },

  /**
   * Preview template with sample data
   * @param {string} id - Template ID
   * @param {Object} sampleData - Sample data for preview
   * @returns {Promise<Object>} Preview result with HTML and metadata
   */
  async previewTemplate(id, sampleData) {
    const endpoint = `${BASE_URL}/v1/templates/${id}/preview`;
    return await execute('POST', endpoint, { sampleData });
  },

  /**
   * Render template with AI generation
   * This calls the backend to generate complete HTML using Gemini AI
   * @param {Object} options - Render options
   * @param {string} options.templateId - Template ID to use
   * @param {Object} options.data - Data context for generation
   * @param {Object} options.context - User context (organizationId, userId)
   * @returns {Promise<Object>} Generated HTML with metadata
   */
  async render(options) {
    const endpoint = `${BASE_URL}/v1/templates/render`;
    return await execute('POST', endpoint, options);
  },

  /**
   * Generate report from PBIX file upload
   * Two-stage AI process:
   * Stage 1: Extract PBIX data and generate realistic sample data
   * Stage 2: Generate complete HTML report with extracted data
   * @param {string} templateId - Template ID to use
   * @param {FormData} formData - Form data with PBIX file and moduleName
   * @returns {Promise<Object>} Generated HTML, extracted data, and metadata
   */
  async generateReportFromPBIX(templateId, formData) {
    const endpoint = `${BASE_URL}/v1/templates/${templateId}/generate-from-pbix`;

    // Get MSAL account and token (same as execute.jsx)
    const account = msalInstance.getActiveAccount();
    if (!account) {
      throw new Error('No active account found. Please login first.');
    }

    try {
      // Acquire token using MSAL
      const authObject = {
        scopes: protectedResources.api.scopes.read,
        account: account,
        redirectUri: '/',
      };

      const result = await msalInstance.acquireTokenSilent(authObject);

      // For file uploads, we need to use fetch directly
      // Do NOT set Content-Type - browser will set it automatically with multipart boundary
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${result.accessToken}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('PBIX upload error:', error);
      throw error;
    }
  },

  /**
   * Get template usage statistics
   * @param {string} id - Template ID
   * @param {Object} filters - Filter parameters (startDate, endDate)
   * @returns {Promise<Object>} Template statistics
   */
  async getTemplateStats(id, filters = {}) {
    const query = new URLSearchParams(filters).toString();
    const endpoint = `${BASE_URL}/v1/templates/${id}/stats${query ? `?${query}` : ''}`;
    return await execute('GET', endpoint);
  },

  // Override Management
  /**
   * Get all overrides for a template
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Array of overrides
   */
  async getOverrides(templateId) {
    const endpoint = `${BASE_URL}/v1/templates/${templateId}/overrides`;
    return await execute('GET', endpoint);
  },

  /**
   * Create template override
   * @param {string} templateId - Template ID
   * @param {Object} data - Override data (scope, organizationId, userId, promptOverrides)
   * @returns {Promise<Object>} Created override
   */
  async createOverride(templateId, data) {
    const endpoint = `${BASE_URL}/v1/templates/${templateId}/overrides`;
    return await execute('POST', endpoint, data);
  },

  /**
   * Update template override
   * @param {string} templateId - Template ID
   * @param {string} overrideId - Override ID
   * @param {Object} data - Updated override data
   * @returns {Promise<Object>} Updated override
   */
  async updateOverride(templateId, overrideId, data) {
    const endpoint = `${BASE_URL}/v1/templates/${templateId}/overrides/${overrideId}`;
    return await execute('PUT', endpoint, data);
  },

  /**
   * Delete template override
   * @param {string} templateId - Template ID
   * @param {string} overrideId - Override ID
   * @returns {Promise<Object>} Success response
   */
  async deleteOverride(templateId, overrideId) {
    const endpoint = `${BASE_URL}/v1/templates/${templateId}/overrides/${overrideId}`;
    return await execute('DELETE', endpoint);
  },

  // Analytics
  /**
   * Get aggregate statistics across all templates
   * @param {Object} filters - Filter parameters (startDate, endDate)
   * @returns {Promise<Object>} Aggregate statistics
   */
  async getAggregateStats(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    const endpoint = `${BASE_URL}/v1/templates/analytics/aggregate${query ? `?${query}` : ''}`;
    return await execute('GET', endpoint);
  },

  /**
   * Get template generation history
   * @param {Object} filters - Filter parameters (startDate, endDate, page, pageSize)
   * @returns {Promise<Object>} Generation history with pagination
   */
  async getGenerationHistory(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    const endpoint = `${BASE_URL}/v1/templates/analytics/history${query ? `?${query}` : ''}`;
    return await execute('GET', endpoint);
  },

  /**
   * Get popular templates ranking
   * @param {Object} filters - Filter parameters (startDate, endDate, limit)
   * @returns {Promise<Object>} Popular templates array
   */
  async getPopularTemplates(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    const endpoint = `${BASE_URL}/v1/templates/analytics/popular${query ? `?${query}` : ''}`;
    return await execute('GET', endpoint);
  },

  // Version Management
  /**
   * Get version history for a template
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Array of template versions
   */
  async getVersionHistory(templateId) {
    const endpoint = `${BASE_URL}/v1/templates/${templateId}/versions`;
    return await execute('GET', endpoint);
  },

  /**
   * Rollback to a specific version
   * @param {string} templateId - Template ID
   * @param {number} version - Version number to rollback to
   * @returns {Promise<Object>} Updated template
   */
  async rollbackToVersion(templateId, version) {
    const endpoint = `${BASE_URL}/v1/templates/${templateId}/versions/${version}/rollback`;
    return await execute('POST', endpoint);
  },

  // Import/Export
  /**
   * Export templates as JSON
   * @param {Array<string>} templateIds - Array of template IDs to export
   * @returns {Promise<Object>} Templates array in export format
   */
  async exportTemplates(templateIds) {
    const endpoint = `${BASE_URL}/v1/templates/export`;
    return await execute('POST', endpoint, { templateIds });
  },

  /**
   * Import templates from JSON
   * @param {Array<Object>} templates - Array of templates to import
   * @returns {Promise<Object>} Import result with success count
   */
  async importTemplates(templates) {
    const endpoint = `${BASE_URL}/v1/templates/import`;
    return await execute('POST', endpoint, { templates });
  },

  // A/B Testing
  /**
   * Get all A/B tests
   * @returns {Promise<Object>} Array of A/B tests
   */
  async getABTests() {
    const endpoint = `${BASE_URL}/v1/templates/ab-tests`;
    return await execute('GET', endpoint);
  },

  /**
   * Create A/B test
   * @param {Object} data - A/B test configuration
   * @returns {Promise<Object>} Created A/B test
   */
  async createABTest(data) {
    const endpoint = `${BASE_URL}/v1/templates/ab-tests`;
    return await execute('POST', endpoint, data);
  },

  /**
   * Get A/B test results
   * @param {string} testId - A/B test ID
   * @returns {Promise<Object>} A/B test results
   */
  async getABTestResults(testId) {
    const endpoint = `${BASE_URL}/v1/templates/ab-tests/${testId}/results`;
    return await execute('GET', endpoint);
  },

  /**
   * End A/B test
   * @param {string} testId - A/B test ID
   * @returns {Promise<Object>} Success response
   */
  async endABTest(testId) {
    const endpoint = `${BASE_URL}/v1/templates/ab-tests/${testId}/end`;
    return await execute('POST', endpoint);
  },
};

export default templateService;
